import {
    AbstractControl,
    ValidationErrors,
    ValidatorFn,
    AsyncValidatorFn,
} from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';

/**
 * VALIDADORES CUSTOM REUTILIZABLES
 * 
 * Colección de validadores comunes que pueden usarse en cualquier formulario.
 * Se pueden combinar múltiples validadores en la configuración del campo.
 */
export class CustomValidators {
    // ============================================================
    // VALIDADORES NUMÉRICOS
    // ============================================================

    /**
     * Validar que un número esté en un rango
     * @example { validators: [CustomValidators.range(0, 100)] }
     */
    static range(min: number, max: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const value = Number(control.value);
            if (isNaN(value)) {
                return { numberExpected: true };
            }

            if (value < min || value > max) {
                return {
                    range: {
                        min,
                        max,
                        actual: value,
                    },
                };
            }

            return null;
        };
    }

    /**
     * Validar mínimo
     */
    static min(min: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const value = Number(control.value);
            if (isNaN(value)) return { numberExpected: true };

            return value < min ? { min: { min, actual: value } } : null;
        };
    }

    /**
     * Validar máximo
     */
    static max(max: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const value = Number(control.value);
            if (isNaN(value)) return { numberExpected: true };

            return value > max ? { max: { max, actual: value } } : null;
        };
    }

    /**
     * Validar que sea entero
     */
    static integer(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const value = Number(control.value);
            return !Number.isInteger(value)
                ? { integer: { value } }
                : null;
        };
    }

    /**
     * Validar que sea positivo
     */
    static positive(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value && control.value !== 0) return null;

            const value = Number(control.value);
            return value <= 0 ? { positive: { value } } : null;
        };
    }

    // ============================================================
    // VALIDADORES DE STRING
    // ============================================================

    /**
     * Validar longitud mínima de string
     */
    static minLength(min: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const length = String(control.value).length;
            return length < min
                ? { minLength: { min, actual: length } }
                : null;
        };
    }

    /**
     * Validar longitud máxima de string
     */
    static maxLength(max: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const length = String(control.value).length;
            return length > max
                ? { maxLength: { max, actual: length } }
                : null;
        };
    }

    /**
     * Validar formato con regex
     * @example { validators: [CustomValidators.pattern(/^\d{3}-\d{4}$/)] }
     */
    static pattern(pattern: RegExp | string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
            return !regex.test(String(control.value))
                ? { pattern: { pattern: pattern.toString(), value: control.value } }
                : null;
        };
    }

    /**
     * Validar email
     */
    static email(): ValidatorFn {
        const emailRegex =
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return this.pattern(emailRegex);
    }

    /**
     * Validar teléfono (formato simple)
     */
    static phone(): ValidatorFn {
        const phoneRegex = /^\+?[\d\s\-()]{9,}$/;
        return this.pattern(phoneRegex);
    }

    /**
     * Validar URL
     */
    static url(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            try {
                new URL(control.value);
                return null;
            } catch {
                return { url: { value: control.value } };
            }
        };
    }

    /**
     * Validar que no contenga espacios
     */
    static noSpaces(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const hasSpaces = /\s/.test(control.value);
            return hasSpaces ? { noSpaces: true } : null;
        };
    }

    /**
     * Validar que solo contenga caracteres alfanuméricos
     */
    static alphanumeric(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const isAlphanumeric = /^[a-zA-Z0-9]+$/.test(control.value);
            return !isAlphanumeric ? { alphanumeric: true } : null;
        };
    }

    // ============================================================
    // VALIDADORES DE COMPARACIÓN
    // ============================================================

    /**
     * Validar que un campo coincida con otro
     * Útil para confirmación de contraseña
     * @example { validators: [CustomValidators.match('password')] }
     */
    static match(fieldName: string): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) return null;

            const form = control.parent;
            if (!form) return null;

            const otherControl = form.get(fieldName);
            if (!otherControl) return null;

            return control.value === otherControl.value
                ? null
                : { match: { fieldName } };
        };
    }

    /**
     * Validador de condicional
     * Solo valida si la condición es verdadera
     */
    static conditional(
        condition: (control: AbstractControl) => boolean,
        validator: ValidatorFn
    ): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!condition(control)) return null;
            return validator(control);
        };
    }

    // ============================================================
    // VALIDADORES ASÍNCRONOS
    // ============================================================

    /**
     * Validar disponibilidad (código único, email único, etc.)
     * @example {
     *   asyncValidators: [
     *     CustomValidators.unique(this.productService.checkProductCode())
     *   ]
     * }
     */
    static unique(
        checkFn: (value: string) => Observable<boolean>
    ): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            if (!control.value) return of(null);

            return checkFn(control.value).pipe(
                map((exists) =>
                    exists ? null : { unique: { value: control.value } }
                ),
                catchError(() => of(null))
            );
        };
    }

    /**
     * Validar después de un delay (evitar múltiples llamadas)
     */
    static asyncValidator(
        validatorFn: (value: any) => Observable<ValidationErrors | null>,
        delayMs: number = 300
    ): AsyncValidatorFn {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            if (!control.value) return of(null);

            return timer(delayMs).pipe(
                switchMap(() => validatorFn(control.value)),
                catchError(() => of(null))
            );
        };
    }

    // ============================================================
    // VALIDADORES PARA TIPOS ESPECÍFICOS
    // ============================================================

    /**
     * Validador para especificación de llantas
     * Valida que width, height y radius sean válidos según estándares
     */
    static tyreSpecs(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = control.value as {
                width?: number;
                height?: number;
                radius?: number;
            } | null;

            if (!value) return null;

            const errors: any = {};

            // Validar ancho (típicamente 100-400)
            if (value.width && (value.width < 100 || value.width > 400)) {
                errors.invalidWidth = { value: value.width };
            }

            // Validar alto (típicamente 20-95)
            if (value.height && (value.height < 20 || value.height > 95)) {
                errors.invalidHeight = { value: value.height };
            }

            // Validar radio (típicamente 10-30)
            if (value.radius && (value.radius < 10 || value.radius > 30)) {
                errors.invalidRadius = { value: value.radius };
            }

            return Object.keys(errors).length > 0 ? errors : null;
        };
    }

    /**
     * Validador para código de producto
     * Formato: CATEGORIA-TIPO-NUMERO (ej: TYRE-SUMMER-001)
     */
    static productCode(): ValidatorFn {
        const pattern = /^[A-Z]{3,}-[A-Z]{3,}-\d{3,}$/;
        return this.pattern(pattern);
    }


    /**
     * Validador que impide fechas en el futuro.
     * Se utiliza como los demás validadores: retorna un `ValidatorFn`
     * que puede añadirse directamente al array `validators` de un campo.
     *
     * Ejemplo de uso en un config:
     * ```ts
     * entryDate: {
     *   type: 'date',
     *   validators: [CustomValidators.noFutureDate()],
     * }
     * ```
     */
    static noFutureDate(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
                return null;
            }

            const dateValue = control.value;
            let checkDate: Date | null = null;

            // Manejar diferentes formatos de entrada
            if (dateValue instanceof Date) {
                checkDate = dateValue;
            } else if (typeof dateValue === 'string') {
                checkDate = new Date(dateValue);
            } else if (typeof dateValue === 'object' && dateValue.year && dateValue.month && dateValue.day) {
                checkDate = new Date(dateValue.year, dateValue.month - 1, dateValue.day);
            }

            if (!checkDate || Number.isNaN(checkDate.getTime())) {
                return null; // La validación de formato se maneja en otros validadores
            }

            // Comparar solo las fechas (sin la hora)
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            checkDate.setHours(0, 0, 0, 0);

            if (checkDate > today) {
                return { futureDateNotAllowed: true };
            }

            return null;
        };
    }


    // ============================================================
    // BUILDER DE VALIDADORES
    // ============================================================

    /**
     * Helper para construir una cadena de validadores
     * @example
     * CustomValidators
     *   .builder()
     *   .required()
     *   .minLength(3)
     *   .maxLength(50)
     *   .pattern(/^[a-z]+$/)
     *   .build()
     */
    static builder(): ValidatorBuilder {
        return new ValidatorBuilder();
    }




}

/**
 * Builder para construir cadenas de validadores de forma fluida
 */
export class ValidatorBuilder {
    private validators: ValidatorFn[] = [];

    required(): this {
        // Validators.required ya está en Angular
        this.validators.push((control) =>
            !control.value ? { required: true } : null
        );
        return this;
    }

    min(value: number): this {
        this.validators.push(CustomValidators.min(value));
        return this;
    }

    max(value: number): this {
        this.validators.push(CustomValidators.max(value));
        return this;
    }

    range(min: number, max: number): this {
        this.validators.push(CustomValidators.range(min, max));
        return this;
    }

    minLength(length: number): this {
        this.validators.push(CustomValidators.minLength(length));
        return this;
    }

    maxLength(length: number): this {
        this.validators.push(CustomValidators.maxLength(length));
        return this;
    }

    pattern(pattern: RegExp | string): this {
        this.validators.push(CustomValidators.pattern(pattern));
        return this;
    }

    email(): this {
        this.validators.push(CustomValidators.email());
        return this;
    }

    phone(): this {
        this.validators.push(CustomValidators.phone());
        return this;
    }

    url(): this {
        this.validators.push(CustomValidators.url());
        return this;
    }

    custom(validator: ValidatorFn): this {
        this.validators.push(validator);
        return this;
    }

    build(): ValidatorFn[] {
        return this.validators;
    }
}
